import React, { useMemo, useState } from 'react';
import {
    List,
    Datagrid,
    TextField,
    FunctionField,
    TopToolbar,
    DataProviderContext,
    useDataProvider,
    Button,
} from 'react-admin';
import { FormControl, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import FormattedDateTimeField from '../../../common/FormattedDateTimeField';
import { NoDataSection } from '../../../common/NoDataSection';
import MonthSelector from '../../../common/MonthSelector';
import { createCustomGoDataProvider, goApi } from '../../../services';
import { Download } from 'lucide-react';

const ReportOrdersList: React.FC = () => {
    const baseDataProvider = useDataProvider();
    const getCurrentMonthRange = () => {
        const now = new Date();

        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
        const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

        const formatDate = (date: Date) => date.toISOString().split('T')[0];

        const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

        return {
            firstDay: formatDate(firstDay),
            lastDay: formatDate(lastDay),
            month: month,
        };
    };

    const [selectedMonthRange, setSelectedMonthRange] = useState(getCurrentMonthRange);
    const [selectedStatus, setSelectedStatus] = useState('');

    const ListActions = () => (
        <TopToolbar>
            <MonthSelector
                monthsToShow={12}
                setSelectedMonthRange={setSelectedMonthRange}
                selectedMonthRange={selectedMonthRange}
            />
            <FormControl
                size="small"
                variant="outlined"
                sx={{ minWidth: 150, backgroundColor: '#fff' }}
            >
                <InputLabel id="report-orders-status-label">Status</InputLabel>
                <Select
                    labelId="report-orders-status-label"
                    value={selectedStatus}
                    label="Status"
                    onChange={(event: SelectChangeEvent) => setSelectedStatus(event.target.value)}
                >
                    <MenuItem value="">All statuses</MenuItem>
                    <MenuItem value="PENDING">PENDING</MenuItem>
                    <MenuItem value="SHIPPED">SHIPPED</MenuItem>
                    <MenuItem value="DELIVERED">DELIVERED</MenuItem>
                    <MenuItem value="CANCELLED">CANCELLED</MenuItem>
                </Select>
            </FormControl>
            <Button label="Export" onClick={handleDownload} startIcon={<Download />} />
        </TopToolbar>
    );
    const listFilter = useMemo(() => {
        return {
            monthYear: selectedMonthRange.month,
            ...(selectedStatus ? { status: selectedStatus } : {}),
        };
    }, [selectedMonthRange, selectedStatus]);

    const customDataProvider = useMemo(() => {
        const dp = createCustomGoDataProvider(baseDataProvider, [
            { resource: 'report_orders', apiEndpoint: '/ecom/reports' },
        ]);

        dp.downloadOrdersReport = async (filters: any = {}) => {
            const query = new URLSearchParams(filters).toString();
            const response = await goApi.get(`/ecom/reports/download?${query}`);
            return response.data;
        };

        return dp;
    }, [baseDataProvider]);

    const handleDownload = async () => {
        try {
            const response = await customDataProvider.downloadOrdersReport(listFilter);

            const fileUrl = response?.url || response?.data?.url;

            if (fileUrl) {
                // window.location.href = fileUrl;
                window.open(fileUrl, '_blank', 'noopener,noreferrer');
            } else {
                console.error('Download URL missing');
            }
        } catch (error) {
            console.error('Download failed', error);
        }
    };
    return (
        <DataProviderContext.Provider value={customDataProvider}>
            <ListActions />
            <List
                resource="report_orders"
                actions={false}
                filter={listFilter}
                empty={<NoDataSection resource="report_orders" />}
            >
                <Datagrid bulkActionButtons={false}>
                    <TextField source="amount" />
                    <TextField source="discount" />
                    <FunctionField
                        label="Product Name"
                        render={(record: any) =>
                            (record.orderItems || [])
                                .map((item: any) => item.productName)
                                .filter(Boolean)
                                .join(', ')
                        }
                    />
                    <FunctionField
                        label="Ordered By"
                        render={(record: any) =>
                            `${record.student?.firstName || ''} ${
                                record.student?.lastName || ''
                            }`.trim()
                        }
                    />
                    <TextField source="student.phone" label="Phone" />
                    <TextField source="id" label="Order Id" />
                    <TextField source="paymentSource" label="Payment Type" />
                    <TextField source="status" />
                    <FormattedDateTimeField source="createdAt" />
                    <FormattedDateTimeField source="expireAt" label="Expire At" />
                </Datagrid>
            </List>
        </DataProviderContext.Provider>
    );
};

export default ReportOrdersList;
